/**
 * en-GB messages added by Phase 2 task W10 (docs/PHASE-2-PLAN.md). Only that task edits this file.
 * Flat keys, `{name}` placeholders, merged into enGB by ./index.ts.
 * `ntf.*` = notification rules, delivery log and the site / group cards. `whk.*` = webhook pages.
 */
export const w10EnGB = {
    // --- Notifications page and rules list ---
    'ntf.title': 'Notifications',
    'ntf.description': 'Choose who is told when something happens, and see what was sent.',
    'ntf.tabs': 'Notifications sections',
    'ntf.tab.rules': 'Rules',
    'ntf.tab.log': 'Delivery log',
    'ntf.rules.caption': 'Notification rules',
    'ntf.rules.where': 'Where',
    'ntf.rules.on': 'On',
    'ntf.rules.switch': 'Rule is on: {name}',
    'ntf.channel.email': 'Email',
    'ntf.channel.sms': 'Text message',
    'ntf.channel.push': 'App notification',
    'ntf.channel.webhook': 'Webhook',
    'ntf.picker.sites': 'Sites',
    'ntf.picker.allSites': 'All sites',
    'ntf.picker.groups': 'Groups',
    'ntf.picker.allGroups': 'All groups',
    'ntf.recipientKind.host': 'Host',
    'ntf.recipientKind.member': 'Member',
    'ntf.recipientKind.reception': 'Reception',
    'ntf.recipientKind.emails': 'Email address',
    'ntf.recipientKind.phones': 'Phone number',
    'ntf.recipientKind.endpoint': 'Webhook',
    'ntf.recipient.host': "The visitor's host",
    'ntf.recipient.member': 'The member who is visiting',
    'ntf.recipient.reception': 'Reception',
    'ntf.recipient.receptionHint': 'Uses the reception contacts set on each site.',
    'ntf.recipient.emails': 'Other email addresses',
    'ntf.recipient.phones': 'Other phone numbers',
    'ntf.recipient.emailsCount': '{count} email addresses',
    'ntf.recipient.phonesCount': '{count} phone numbers',
    'ntf.recipient.endpoint': 'Webhook endpoint',
    'ntf.recipient.endpointHint': 'Only endpoints that are not paused are listed.',
    'ntf.recipient.endpointShort': 'Webhook',
    'ntf.recipient.chooseEndpoint': 'Choose an endpoint',
    // --- Rule builder ---
    'ntf.rule.add': 'Add a rule',
    'ntf.rule.newTitle': 'New notification rule',
    'ntf.rule.editTitle': 'Edit notification rule',
    'ntf.rule.description': 'Say when it fires, who gets it and what it says.',
    'ntf.rule.when': 'When',
    'ntf.rule.name': 'Rule name',
    'ntf.rule.nameRequired': 'Give the rule a name.',
    'ntf.rule.trigger': 'What happens',
    'ntf.rule.enabled': 'Rule is on',
    'ntf.rule.pickSite': 'Choose at least one site, or tick all sites.',
    'ntf.rule.pickGroup': 'Choose at least one group, or tick all groups.',
    'ntf.rule.filters': 'Only when',
    'ntf.rule.filtersHint': 'Extra conditions. Every one must match.',
    'ntf.rule.noFilters': 'No extra conditions: the rule fires every time.',
    'ntf.rule.send': 'Send to',
    'ntf.rule.channel': 'How',
    'ntf.rule.recipients': 'Who',
    'ntf.rule.message': 'Message',
    'ntf.rule.messageHint': 'Leave the subject and message empty to use the standard wording. Merge fields are filled in when it is sent.',
    'ntf.rule.subject': 'Subject',
    'ntf.rule.body': 'Message',
    'ntf.rule.bodyEmailHint': 'Answers to sensitive questions are never included.',
    'ntf.rule.bodyTextHint': 'Plain text. Use the list below to add details such as the visitor name.',
    'ntf.rule.webhookNote': 'A webhook sends the details in a fixed format, so there is no message to write.',
    'ntf.merge.choose': 'Add a merge field',
    'ntf.merge.insertSubject': 'Merge field for the subject',
    'ntf.merge.insertBody': 'Merge field for the message',
    'ntf.filter.add': 'Add a condition',
    'ntf.filter.addButton': 'Add condition',
    'ntf.filter.remove': 'Remove',
    'ntf.filter.field': 'Question',
    'ntf.filter.chooseField': 'Choose a question',
    'ntf.filter.fieldsFrom': 'Questions are taken from {site}.',
    'ntf.filter.value': 'Answer',
    'ntf.filter.is': 'Is',
    'ntf.filter.methods': 'Sign-in methods',
    'ntf.filter.methodsHint': 'Separate with commas, for example kiosk, contactless.',
    'ntf.filter.kind.field_equals': 'An answer equals',
    'ntf.filter.kind.field_contains': 'An answer contains',
    'ntf.filter.kind.field_is_set': 'A question was answered',
    'ntf.filter.kind.host_is_set': 'A host was chosen',
    'ntf.filter.kind.method_in': 'Signed in by',
    'ntf.filter.kind.is_member': 'The person is a member',
    'ntf.preview.title': 'Preview',
    'ntf.preview.button': 'Show preview',
    'ntf.preview.empty': 'Show a preview with sample details to check the wording.',
    'ntf.preview.subject': 'Subject',
    'ntf.preview.recipient': '{kind}: {count}',
    // --- Delivery log ---
    'ntf.log.caption': 'Notifications sent',
    'ntf.log.when': 'When',
    'ntf.log.to': 'Sent to',
    'ntf.log.attempts': 'Tries',
    'ntf.log.site': 'Site',
    'ntf.log.dates': 'Dates',
    'ntf.status.queued': 'Waiting',
    'ntf.status.sent': 'Sent',
    'ntf.status.logged': 'Recorded only',
    'ntf.status.skipped': 'Not sent',
    'ntf.status.failed': 'Failed',
    // --- Site reception card and group host-alert card ---
    'ntf.reception.title': 'Reception contacts',
    'ntf.reception.description': 'Who "reception" means at this site, for notification rules that tell reception.',
    'ntf.reception.emails': 'Reception email addresses',
    'ntf.reception.phones': 'Reception phone numbers',
    'ntf.hostAlerts.title': 'Notifications',
    'ntf.hostAlerts.description': 'Tell members when their visitors arrive or leave.',
    'ntf.hostAlerts.signIn': 'When a visitor signs in, tell the host by',
    'ntf.hostAlerts.signOut': 'When a visitor signs out, tell the host by',
    'ntf.hostAlerts.quickReplies': 'Let hosts reply from the notification',
    'ntf.hostAlerts.quickRepliesHint': 'For example "On my way" or "Please wait". Needs app notifications.',
    'ntf.hostAlerts.allowList': 'Answers that may appear in notifications',
    'ntf.hostAlerts.allowListHint': 'Only the questions you tick can appear in a message about a visit to this group. Sensitive questions are never offered.',
    'ntf.hostAlerts.noFields': 'There are no questions to choose from.',
    // --- Webhooks ---
    'whk.title': 'Webhooks',
    'whk.description': 'Send sign-in events to other systems as soon as they happen.',
    'whk.add': 'Add an endpoint',
    'whk.caption': 'Webhook endpoints',
    'whk.url': 'Address (URL)',
    'whk.events': 'Events',
    'whk.eventsCount': '{count} events',
    'whk.lastSuccess': 'Last success',
    'whk.never': 'Never',
    'whk.managed': 'Managed by a linked system',
    'whk.managedNote': 'This endpoint is managed by {module}. It can be looked at here but not changed.',
    'whk.status.active': 'Working',
    'whk.status.paused': 'Paused',
    'whk.status.failing': 'Failing',
    'whk.new': 'New webhook endpoint',
    'whk.newDescription': 'Events are sent to this address, signed with a secret that is shown once.',
    'whk.create': 'Create endpoint',
    'whk.detailDescription': 'The signing secret ends in {hint}.',
    'whk.failingSince': 'Failing since {when}.',
    'whk.lastSuccessAt': 'Last success {when}.',
    'whk.pause': 'Pause',
    'whk.resume': 'Resume',
    'whk.sendTest': 'Send a test',
    'whk.rotate': 'Replace secret',
    'whk.rotateConfirm': 'Yes, replace the secret',
    'whk.test.ok': 'The test was received (status {code}, {ms} ms).',
    'whk.test.failed': 'The test did not get through: {reason}.',
    'whk.secret.title': 'Webhook signing secret',
    'whk.secret.description': 'Use this to check that events really come from CloudSign. It is shown only once.',
    'whk.secret.label': 'Signing secret',
    'whk.rotated.title': 'New signing secret',
    'whk.rotated.description': 'The old secret still works for 24 hours. This one is shown only once.',
    'whk.form.endpoint': 'Endpoint',
    'whk.form.nameRequired': 'Give the endpoint a name.',
    'whk.form.urlInvalid': 'Enter an address that starts with https://.',
    'whk.form.urlHint': 'Must be a public https:// address.',
    'whk.form.eventsHint': 'Choose the events this endpoint should receive.',
    'whk.form.eventsRequired': 'Choose at least one event.',
    'whk.form.options': 'Options',
    'whk.form.profile': 'Details sent',
    'whk.form.profileHint': 'Minimal sends only the visit times and an id, with no personal details.',
    'whk.profile.standard': 'Standard',
    'whk.profile.minimal': 'Minimal',
    'whk.form.skipSources': 'Do not send events caused by',
    'whk.form.skipSourcesHint': 'Separate with commas, for example omp. Stops loops with a linked system.',
    'whk.form.alertAfter': 'Warn me after (minutes)',
    'whk.form.alertAfterHint': 'How long it can keep failing before an email is sent. 0 turns the warning off.',
    'whk.form.alertEmail': 'Warning email address',
    'whk.form.alertEmailHint': "Leave empty to email the organisation's administrators.",
    'whk.form.ordered': 'Send in order',
    'whk.form.orderedHint': 'Wait for an older event to succeed before sending newer ones.',
    'whk.event.visit.signed_in': 'Visitor signed in',
    'whk.event.visit.signed_out': 'Visitor signed out',
    'whk.event.visit.updated': 'Visit changed',
    'whk.event.visit.deleted': 'Visit deleted',
    'whk.event.preregistration.created': 'Pre-registration created',
    'whk.event.preregistration.updated': 'Pre-registration changed',
    'whk.event.preregistration.cancelled': 'Pre-registration cancelled',
    'whk.event.preregistration.arrived': 'Pre-registered visitor arrived',
    'whk.event.member.created': 'Member added',
    'whk.event.member.updated': 'Member changed',
    'whk.event.member.archived': 'Member archived',
    'whk.event.feedback.received': 'Feedback received',
    'whk.event.notification': 'Notification rule fired',
    'whk.event.test': 'Test',
    'whk.delivery.queued': 'Waiting',
    'whk.delivery.sending': 'Sending',
    'whk.delivery.retrying': 'Trying again',
    'whk.delivery.succeeded': 'Delivered',
    'whk.delivery.abandoned': 'Gave up',
    'whk.log.title': 'Deliveries',
    'whk.log.caption': 'Webhook deliveries',
    'whk.log.event': 'Event',
    'whk.log.code': 'Last status',
    'whk.log.details': 'Details',
    'whk.log.hide': 'Hide',
    'whk.log.nextTry': 'Next try {when}',
    'whk.log.attempts': 'Attempts',
    'whk.log.noAttempts': 'No attempts yet.',
    'whk.log.attemptNumber': 'Attempt {n}',
    'whk.log.statusCode': 'Status {code}',
    'whk.log.duration': '{ms} ms',
    'whk.log.payload': 'What was sent',
    'whk.log.payloadCleared': 'The details have been cleared to save space.',
    'whk.log.resend': 'Send again',
    'whk.log.resent': 'Queued to send again.',
};
//# sourceMappingURL=w10.js.map